import { gql } from '@apollo/client';

export const CreditAgreementDocumentMetadataFields = gql`
  fragment CreditAgreementDocumentMetadataFields on CreditAgreementMetadata {
    id
    facilityType
    creditFacilityId
    effectiveFrom
    effectiveTo
    version
    _schemaVersion_
    s3Path
    _note_
    displayName
  }
`;

export const CreditAgreementArtifactFields = gql`
  fragment CreditAgreementArtifactFields on CreditAgreementArtifact {
    id
    creditAgreement
  }
`;

export const CreditAgreementFullArtifactFields = gql`
  fragment CreditAgreementFullArtifactFields on CreditAgreementFullArtifact {
    id
    creditAgreement
  }
`;

// ========== QUERIES ==========

export const GET_CREDIT_AGREEMENT_DOCUMENTS_METADATA = gql`
  ${CreditAgreementDocumentMetadataFields}
  query GetCreditAgreementDocumentsMetadata($creditFacilityId: UUID!) {
    creditAgreementDocumentsMetadata(creditFacilityId: $creditFacilityId) {
      ...CreditAgreementDocumentMetadataFields
    }
  }
`;

export const GET_CREDIT_AGREEMENT_FULL_ARTIFACT_WIP = gql`
  ${CreditAgreementFullArtifactFields}
  query GetCreditAgreementFullArtifactWip($creditFacilityId: UUID!) {
    creditAgreementFullArtifactWip(creditFacilityId: $creditFacilityId) {
      wip {
        ...CreditAgreementFullArtifactFields
      }
      errors
    }
  }
`;

export const GET_CREDIT_AGREEMENT_FULL_ARTIFACT = gql`
  ${CreditAgreementFullArtifactFields}
  query GetCreditAgreementFullArtifact($creditAgreementId: UUID!) {
    creditAgreementFullArtifact(creditAgreementId: $creditAgreementId) {
      ...CreditAgreementFullArtifactFields
    }
  }
`;

export const GET_CREDIT_AGREEMENT_ARTIFACT = gql`
  ${CreditAgreementArtifactFields}
  query GetCreditAgreementArtifact($creditAgreementId: UUID!) {
    creditAgreementArtifact(creditAgreementId: $creditAgreementId) {
      ...CreditAgreementArtifactFields
    }
  }
`;

export const GET_CURRENT_CREDIT_AGREEMENT_FULL_ARTIFACT = gql`
  ${CreditAgreementFullArtifactFields}
  query GetCurrentCreditAgreementFullArtifact($creditFacilityId: UUID!) {
    currentCreditAgreementFullArtifact(creditFacilityId: $creditFacilityId) {
      ...CreditAgreementFullArtifactFields
    }
  }
`;

export const GET_CURRENT_CREDIT_AGREEMENT_FULL_ARTIFACTS_FOR_ORGANIZATION = gql`
  ${CreditAgreementFullArtifactFields}
  query GetCurrentCreditAgreementFullArtifactsForOrganization(
    $organizationId: UUID!
    $creditFacilitySlugsToHide: [String!]!
  ) {
    organization(
      organizationId: $organizationId
      creditFacilitySlugsToHide: $creditFacilitySlugsToHide
    ) {
      currentCreditAgreementArtifacts {
        ...CreditAgreementArtifactFields
      }
    }
  }
`;

export const GET_CURRENT_CREDIT_AGREEMENT_FULL_ARTIFACTS_FOR_CREDIT_FACILITIES = gql`
  query GetCurrentCreditAgreementFullArtifactsForCreditFacilities(
    $creditFacilityIds: [UUID!]!
  ) {
    currentCreditAgreementFullArtifacts(creditFacilityIds: $creditFacilityIds) {
      id
      creditAgreement
      creditFacilityId
    }
  }
`;

export const DOES_CREDIT_AGREEMENT_WIP_EXIST = gql`
  query DoesCreditAgreementWipExist($creditFacilityId: UUID!) {
    doesCreditAgreementWipExist(creditFacilityId: $creditFacilityId)
  }
`;

export const GET_CREDIT_AGREEMENT_DOWNLOAD_URL = gql`
  query GetCreditAgreementDownloadUrl($creditFacilityId: UUID!) {
    getCreditAgreementS3Path(creditFacilityId: $creditFacilityId) {
      downloadUrl
      fileName
    }
  }
`;

export const GET_CREDIT_AGREEMENT_BORROWER_ENTITY_NAME = gql`
  query GetCreditAgreementBorrowerEntityName($creditFacilityId: UUID!) {
    creditAgreementBorrowerEntityName(creditFacilityId: $creditFacilityId)
  }
`;

export const GET_CREDIT_AGREEMENT_ELIGIBILITY_TESTS_BASE_FIELDS = gql`
  query GetCreditAgreementEglibilityTestsBaseFields($organizationId: UUID!) {
    creditAgreementEligibilityTestsBaseFields(organizationId: $organizationId)
  }
`;

export const GET_CREDIT_AGREEMENT_CAPITAL_PROVIDER_ORGANIZATIONS = gql`
  query GetCreditAgreementCapitalProviderOrganizations(
    $creditFacilityIds: [UUID!]!
  ) {
    creditAgreementCapitalProviderOrganizations(
      creditFacilityIds: $creditFacilityIds
    ) {
      id
      name
    }
  }
`;

export const CONVERT_CREDIT_AGREEMENT_FULL_ARTIFACT_IDS = gql`
  query ConvertCreditAgreementFullArtifactIds(
    $creditAgreementFullArtifact: CreditAgreementFullArtifactInput!
  ) {
    convertCreditAgreementFullArtifactIds(
      creditAgreementFullArtifact: $creditAgreementFullArtifact
    ) {
      creditAgreement
    }
  }
`;

export const EVALUATE_PORTFOLIO_COVENANT_POSTGRES_QUERY = gql`
  query EvaluatePortfolioCovenantPostgresQuery(
    $creditAgreementId: UUID!
    $postgresQuery: PostgresQuery!
  ) {
    evaluatePortfolioCovenantPostgresQuery(
      creditAgreementId: $creditAgreementId
      postgresQuery: $postgresQuery
    ) {
      result {
        name
        value
      }
      warnings
      inputs {
        ... on PrincipalBalanceDailyAverageInputValues {
          type
          inputValues {
            date
            value
          }
        }
        ... on TAccountsSumInputValues {
          type
          inputValues {
            tAccount
            counterpartySlug
            value
          }
        }
        ... on OrgProvidedInputInputValues {
          type
          inputValues {
            reportingDate
            version
            orgProvidedInputValue
          }
        }
      }
    }
  }
`;

export const GET_LOAN_MECHANICS_SERIALIZED = gql`
  query GetLoanMechanicsSerialized($creditFacilityId: UUID!) {
    loanMechanicsSerialized(creditFacilityId: $creditFacilityId)
  }
`;

// ========== MUTATIONS ==========
export const REMOVE_CREDIT_AGREEMENT_FULL_ARTIFACT_WIP = gql`
  mutation RemoveCreditAgreementFullArtifactWip($creditFacilityId: UUID!) {
    removeCreditAgreementFullArtifactWip(creditFacilityId: $creditFacilityId) {
      response
    }
  }
`;

export const UPDATE_CREDIT_AGREEMENT_FULL_ARTIFACT_WIP = gql`
  mutation UpdateCreditAgreementFullArtifactWip(
    $creditAgreementFullArtifactWip: CreditAgreementFullArtifactInput!
  ) {
    updateCreditAgreementFullArtifactWip(
      creditAgreementFullArtifactWip: $creditAgreementFullArtifactWip
    ) {
      response
    }
  }
`;

export const UPDATE_CLIENT_CREDIT_AGREEMENT_FULL_ARTIFACT_WIP = gql`
  mutation UpdateClientCreditAgreementFullArtifactWip(
    $creditAgreementFullArtifactWip: CreditAgreementFullArtifactInput!
  ) {
    updateClientCreditAgreementFullArtifactWip(
      creditAgreementFullArtifactWip: $creditAgreementFullArtifactWip
    ) {
      response
    }
  }
`;

export const EVALUATE_PORTFOLIO_COVENANTS = gql`
  mutation EvaluatePortfolioCovenants(
    $evaluatePortfolioCovenantsRequest: EvaluatePortfolioCovenantsRequest!
  ) {
    evaluatePortfolioCovenants(
      evaluatePortfolioCovenantsRequest: $evaluatePortfolioCovenantsRequest
    ) {
      response
      portfolioCovenantIds
    }
  }
`;

export const EVALUATE_DEV_PORTFOLIO_COVENANT_WORKLEASE = gql`
  mutation EvaluateDevPortfolioCovenantWorklease(
    $worklease: EvaluatePortfolioCovenantsRequest!
  ) {
    result: evaluateDevPortfolioCovenantWorklease(worklease: $worklease) {
      response
    }
  }
`;

export const COMMIT_ARTIFACT_DOCUMENT_AND_CREDIT_AGREEMENTS = gql`
  mutation commitArtifactDocumentAndCreditAgreements($creditFacilityId: UUID!) {
    commitArtifactDocumentAndCreditAgreements(
      creditFacilityId: $creditFacilityId
    ) {
      successes
      errors
    }
  }
`;

export const COMMIT_ARTIFACT_DOCUMENT_AND_CREDIT_AGREEMENTS_FOR_PIPELINE_DEFINITION = gql`
  mutation commitArtifactDocumentAndCreditAgreementsForPipelineDefinition(
    $pipelineDefinitionId: UUID!
    $creditFacilityId: UUID
  ) {
    commitArtifactDocumentAndCreditAgreements(
      pipelineDefinitionId: $pipelineDefinitionId
      creditFacilityId: $creditFacilityId
    ) {
      successes
      errors
    }
  }
`;
